import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  type Row,
  useReactTable,
} from "@tanstack/react-table";
import {
  Check,
  Copy,
  GitCompareArrows,
  RefreshCw,
} from "lucide-react";
import { motion, useAnimation, type Variants } from "motion/react";
import {
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type UIEvent,
} from "react";

import type { ComparisonFilters, Opportunity } from "../types";
import {
  calculateTradeEstimate,
  type PriceOverride,
} from "../metrics";
import { useCurrency } from "../../currency/CurrencyContext";
import type { Market } from "../../markets/api";
import { ActiveFilterChips } from "./ActiveFilterChips";
import { DealDetails } from "./DealDetails";

interface ComparisonTableProps {
  rows: Opportunity[];
  totalMatches: number;
  isInitialLoading: boolean;
  isRefreshing: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadingRowCount: number;
  errorMessage: string | null;
  onLoadMore: () => void;
  filters: ComparisonFilters;
  markets: Market[];
  searchQuery: string;
  onFiltersChange: (filters: ComparisonFilters) => void;
  onSearchQueryChange: (value: string) => void;
  savedByKey: ReadonlyMap<
    string,
    { is_deal: boolean; is_alert: boolean }
  >;
  onSaveOpportunity: (opportunity: Opportunity, kind: "deal" | "alert") => void;
}

export function getOpportunityKey(opportunity: Opportunity): string {
  return `${opportunity.skin_id}:${opportunity.buy_market}:${opportunity.sell_market}:${opportunity.buy_price}:${opportunity.sell_price}`;
}

function formatPercent(value: number): string {
  return `${value >= 0 ? "+" : ""}${new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}%`;
}

const BOOKMARK_VARIANTS = {
  normal: { scaleY: 1, scaleX: 1 },
  animate: {
    scaleY: [1, 1.3, 0.9, 1.05, 1],
    scaleX: [1, 0.9, 1.1, 0.95, 1],
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
} satisfies Variants;

const BOOKMARK_PATH =
  "m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z";

const HEART_PATH =
  "M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z";

const HEART_VARIANTS = {
  normal: { scale: 1 },
  animate: {
    scale: [1, 0.9, 1.2, 1],
    transition: { duration: 0.6, ease: "easeInOut" },
  },
} satisfies Variants;

function BookmarkIcon({ isSaved = false }: { isSaved?: boolean }) {
  const controls = useAnimation();
  const clipId = `bookmark-fill-${useId().replace(/:/g, "")}`;

  return (
    <svg
      className="row-save-bookmark"
      height="13"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="13"
      xmlns="http://www.w3.org/2000/svg"
      onMouseEnter={() => void controls.start("animate")}
      onMouseLeave={() => void controls.start("normal")}
      onClick={() => void controls.start("animate")}
      aria-hidden="true"
    >
      <defs>
        <clipPath id={clipId}>
          <motion.circle
            cx="12"
            cy="12"
            r={isSaved ? 20 : 0}
            animate={{ r: isSaved ? 20 : 0 }}
            initial={false}
            transition={{
              type: "spring",
              stiffness: 280,
              damping: 20,
              mass: 0.7,
            }}
          />
        </clipPath>
      </defs>
      <motion.g
        initial="normal"
        animate={controls}
        style={{ originY: 0.5, originX: 0.5 }}
        variants={BOOKMARK_VARIANTS}
      >
        <path d={BOOKMARK_PATH} fill="currentColor" clipPath={`url(#${clipId})`} />
        <path d={BOOKMARK_PATH} fill="none" stroke="currentColor" />
      </motion.g>
    </svg>
  );
}

function HeartIcon({ isSaved = false }: { isSaved?: boolean }) {
  const [visualSaved, setVisualSaved] = useState(isSaved);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    setVisualSaved(isSaved);
  }, [isSaved]);

  const handleClick = () => {
    setVisualSaved((value) => !value);
    setIsAnimating(false);
    requestAnimationFrame(() => setIsAnimating(true));
  };

  return (
    <svg
      className="row-save-heart"
      height="13"
      width="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
      onClick={handleClick}
      aria-hidden="true"
    >
      <motion.g
        initial="normal"
        animate={isAnimating ? "animate" : "normal"}
        variants={HEART_VARIANTS}
        style={{ originX: 0.5, originY: 0.5 }}
        onAnimationComplete={() => setIsAnimating(false)}
      >
        <motion.path
          d={HEART_PATH}
          fill="currentColor"
          stroke="none"
          initial={{ fillOpacity: isSaved ? 1 : 0 }}
          animate={{ fillOpacity: visualSaved ? 1 : 0 }}
          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        />
        <path d={HEART_PATH} fill="none" />
      </motion.g>
    </svg>
  );
}

const WEAR_SUFFIX_PATTERN =
  /\s+(\((?:Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\))$/;

function splitItemWear(hashName: string): {
  name: string;
  wear: string | null;
} {
  const match = hashName.match(WEAR_SUFFIX_PATTERN);
  if (!match || match.index === undefined) {
    return { name: hashName, wear: null };
  }
  return {
    name: hashName.slice(0, match.index),
    wear: match[1],
  };
}

async function copyToClipboard(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) {
    throw new Error("Clipboard copy failed");
  }
}

function relativeTime(value: string | null, now: number): string {
  if (!value) {
    return "нет времени";
  }
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) {
    return "нет времени";
  }

  const seconds = Math.max(0, Math.floor((now - timestamp) / 1000));
  if (seconds < 60) {
    return `${seconds} сек. назад`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} мин. назад`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} ч. назад`;
  }
  return `${Math.floor(hours / 24)} дн. назад`;
}

function MarketCell({
  iconUrl,
  name,
  offers,
  link,
}: {
  iconUrl: string | null;
  name: string;
  offers: number;
  link: string | null;
}) {
  return (
    <div className="row-market">
      {iconUrl ? (
        <img src={iconUrl} alt="" />
      ) : (
        <span className="market-logo-fallback">{name.slice(0, 1)}</span>
      )}
      <div>
        {link ? (
          <a
            className="market-name-link"
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            title={`Открыть ${name}`}
            onClick={(event) => event.stopPropagation()}
          >
            <strong>{name}</strong>
          </a>
        ) : (
          <strong>{name}</strong>
        )}
        <small>Офферы: {offers}</small>
      </div>
    </div>
  );
}

const columnHelper = createColumnHelper<Opportunity>();
const DETAILS_TRANSITION_MS = 380;

interface DealDetailsRevealProps {
  opportunity: Opportunity;
  isExpanded: boolean;
  visibleColumns: number;
  profitableSpreadPct: number;
  priceOverride?: PriceOverride;
  onPriceSelect: (tone: "buy" | "sell", price: number) => void;
}

const DealDetailsReveal = memo(function DealDetailsReveal({
  opportunity,
  isExpanded,
  visibleColumns,
  profitableSpreadPct,
  priceOverride,
  onPriceSelect,
}: DealDetailsRevealProps) {
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const mountedRef = useRef(false);

  useEffect(() => {
    let firstFrame: number | null = null;
    let secondFrame: number | null = null;
    let closeTimer: number | null = null;

    if (isExpanded) {
      mountedRef.current = true;
      firstFrame = window.requestAnimationFrame(() => {
        setIsMounted(true);
        secondFrame = window.requestAnimationFrame(() => setIsVisible(true));
      });
    } else if (mountedRef.current) {
      firstFrame = window.requestAnimationFrame(() => {
        setIsVisible(false);
        closeTimer = window.setTimeout(() => {
          mountedRef.current = false;
          setIsMounted(false);
        }, DETAILS_TRANSITION_MS);
      });
    }

    return () => {
      if (firstFrame !== null) {
        window.cancelAnimationFrame(firstFrame);
      }
      if (secondFrame !== null) {
        window.cancelAnimationFrame(secondFrame);
      }
      if (closeTimer !== null) {
        window.clearTimeout(closeTimer);
      }
    };
  }, [isExpanded]);

  return (
    <tr
      className={`deal-details-row${isMounted ? " has-content" : ""}${
        isVisible ? " is-open" : ""
      }`}
      aria-hidden={!isExpanded}
    >
      <td colSpan={visibleColumns}>
        <div className="deal-details-reveal">
          <div className="deal-details-reveal-inner">
            {isMounted && (
              <DealDetails
                opportunity={opportunity}
                profitableSpreadPct={profitableSpreadPct}
                priceOverride={priceOverride}
                onPriceSelect={onPriceSelect}
              />
            )}
          </div>
        </div>
      </td>
    </tr>
  );
});

function TableSkeletonRows({
  count,
  visibleColumns,
}: {
  count: number;
  visibleColumns: number;
}) {
  return (
    <tbody className="table-skeleton-body" aria-hidden="true">
      {Array.from({ length: count }, (_, rowIndex) => (
        <tr className="table-skeleton-row" key={rowIndex}>
          {Array.from({ length: visibleColumns }, (_, cellIndex) => (
            <td key={cellIndex}>
              <span />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

interface DealTableRowProps {
  row: Row<Opportunity>;
  currencyCode: string;
  isExpanded: boolean;
  visibleColumns: number;
  profitableSpreadPct: number;
  onToggle: (rowId: string) => void;
  priceOverride?: PriceOverride;
  onPriceSelect: (rowId: string, tone: "buy" | "sell", price: number) => void;
}

const DealTableRow = memo(function DealTableRow({
  row,
  currencyCode,
  isExpanded,
  visibleColumns,
  profitableSpreadPct,
  onToggle,
  priceOverride,
  onPriceSelect,
}: DealTableRowProps) {
  void currencyCode;
  return (
    <tbody className="deal-row-group">
      <tr
        className={`deal-row${isExpanded ? " is-expanded" : ""}`}
        tabIndex={0}
        aria-expanded={isExpanded}
        onClick={() => onToggle(row.id)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggle(row.id);
          }
        }}
      >
        {row.getVisibleCells().map((cell) => (
          <td key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
      <DealDetailsReveal
        opportunity={row.original}
        isExpanded={isExpanded}
        visibleColumns={visibleColumns}
        profitableSpreadPct={profitableSpreadPct}
        priceOverride={priceOverride}
        onPriceSelect={(tone, price) => onPriceSelect(row.id, tone, price)}
      />
    </tbody>
  );
});

export function ComparisonTable({
  rows,
  totalMatches,
  isInitialLoading,
  isRefreshing,
  hasMore,
  isLoadingMore,
  loadingRowCount,
  errorMessage,
  onLoadMore,
  filters,
  markets,
  searchQuery,
  onFiltersChange,
  onSearchQueryChange,
  savedByKey,
  onSaveOpportunity,
}: ComparisonTableProps) {
  const profitableSpreadPct = filters.profitableSpreadPct;
  const { currency, formatPrice } = useCurrency();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [priceOverrides, setPriceOverrides] = useState<
    Record<string, PriceOverride>
  >({});
  const [now, setNow] = useState(() => Date.now());
  const [copyNoticeId, setCopyNoticeId] = useState<number | null>(null);
  const copyNoticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadMoreRequested = useRef(false);
  useEffect(() => {
    if (!isLoadingMore) {
      loadMoreRequested.current = false;
    }
  }, [isLoadingMore, rows.length]);
  useEffect(
    () => () => {
      if (copyNoticeTimer.current) {
        clearTimeout(copyNoticeTimer.current);
      }
    },
    [],
  );
  useEffect(() => {
    if (!rows.length) {
      return;
    }
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [rows.length]);
  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const container = event.currentTarget;
      const remaining =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      const threshold = Math.max(360, container.clientHeight * 0.45);
      if (
        hasMore &&
        !isLoadingMore &&
        !loadMoreRequested.current &&
        remaining <= threshold
      ) {
        loadMoreRequested.current = true;
        onLoadMore();
      }
    },
    [hasMore, isLoadingMore, onLoadMore],
  );
  const toggleRow = useCallback(
    (rowId: string) =>
      setExpandedId((current) => (current === rowId ? null : rowId)),
    [],
  );
  const selectLadderPrice = useCallback(
    (rowId: string, tone: "buy" | "sell", price: number) => {
      setPriceOverrides((current) => {
        const currentOverride = current[rowId] ?? {};
        const key = tone === "buy" ? "buyPrice" : "sellPrice";
        if (currentOverride[key] === price) {
          const remaining = { ...currentOverride };
          delete remaining[key];
          if (Object.keys(remaining).length === 0) {
            const next = { ...current };
            delete next[rowId];
            return next;
          }
          return { ...current, [rowId]: remaining };
        }
        return {
          ...current,
          [rowId]: { ...currentOverride, [key]: price },
        };
      });
    },
    [],
  );
  const copyItemName = useCallback(async (hashName: string) => {
    try {
      await copyToClipboard(hashName);
      setCopyNoticeId(Date.now());
      if (copyNoticeTimer.current) {
        clearTimeout(copyNoticeTimer.current);
      }
      copyNoticeTimer.current = setTimeout(() => {
        setCopyNoticeId(null);
        copyNoticeTimer.current = null;
      }, 2200);
    } catch {
      setCopyNoticeId(null);
    }
  }, []);
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "item",
        header: "Предмет",
        cell: ({ row }) => {
          const itemName = splitItemWear(row.original.hash_name);
          return (
            <div className="row-item">
              {row.original.skin_icon_url ? (
                <img src={row.original.skin_icon_url} alt="" />
              ) : (
                <span className="skin-image-fallback" />
              )}
              <div className="row-item-copy" title={row.original.hash_name}>
                <button
                  className="copyable-item-name"
                  type="button"
                  title={`Копировать: ${row.original.hash_name}`}
                  aria-label={`Копировать название ${row.original.hash_name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    void copyItemName(row.original.hash_name);
                  }}
                >
                  <strong>{itemName.name}</strong>
                  <Copy aria-hidden="true" />
                </button>
                {itemName.wear && <small>{itemName.wear}</small>}
              </div>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "buy-market",
        header: "Покупка",
        cell: ({ row }) => (
          <MarketCell
            iconUrl={row.original.buy_market_icon_url}
            name={row.original.buy_market_name}
            offers={row.original.buy_available_count}
            link={row.original.buy_link}
          />
        ),
      }),
      columnHelper.accessor("buy_price", {
        header: "Цена покупки",
        cell: ({ row }) => {
          const estimate = calculateTradeEstimate(
            row.original,
            priceOverrides[row.id],
          );
          return <strong className="row-price">{formatPrice(estimate.buyPrice)}</strong>;
        },
      }),
      columnHelper.display({
        id: "sell-market",
        header: "Продажа",
        cell: ({ row }) => (
          <MarketCell
            iconUrl={row.original.sell_market_icon_url}
            name={row.original.sell_market_name}
            offers={row.original.sell_available_count}
            link={row.original.sell_link}
          />
        ),
      }),
      columnHelper.accessor("sell_price", {
        header: "Цена продажи",
        cell: ({ row }) => {
          const estimate = calculateTradeEstimate(
            row.original,
            priceOverrides[row.id],
          );
          return <strong className="row-price">{formatPrice(estimate.sellPrice)}</strong>;
        },
      }),
      columnHelper.display({
        id: "profit",
        header: "Чистая прибыль",
        cell: ({ row }) => (
          (() => {
            const estimate = calculateTradeEstimate(
              row.original,
              priceOverrides[row.id],
            );
            return (
              <div
                className={`row-profit${
                  estimate.profitAbs < 0 ? " is-negative" : ""
                }`}
              >
                <strong>{formatPrice(estimate.profitAbs)}</strong>
                <small>{formatPercent(estimate.profitPct)}</small>
              </div>
            );
          })()
        ),
      }),
      columnHelper.display({
        id: "updates",
        header: "Обновления",
        cell: ({ row }) => (
          <div className="row-updates">
            <div>
              <strong>{row.original.buy_market_name}</strong>
              <span>
                {row.original.buy_market_stats.avg_daily_sales ?? "—"} продаж
                за день
              </span>
              <time>{relativeTime(row.original.buy_observed_at, now)}</time>
            </div>
            <div>
              <strong>{row.original.sell_market_name}</strong>
              <span>
                {row.original.sell_market_stats.avg_daily_sales ?? "—"} продаж
                за день
              </span>
              <time>{relativeTime(row.original.sell_observed_at, now)}</time>
            </div>
          </div>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const saved = savedByKey.get(getOpportunityKey(row.original));
          return (
            <div className="row-save-actions">
              <button
                className={`row-save-action${saved?.is_deal ? " is-saved" : ""}`}
                type="button"
                title={saved?.is_deal ? "Убрать из сделок" : "Добавить в сделки"}
                aria-label={saved?.is_deal ? "Убрать из сделок" : "Добавить в сделки"}
                onClick={(event) => {
                  event.stopPropagation();
                  onSaveOpportunity(row.original, "deal");
                }}
              >
                <BookmarkIcon isSaved={saved?.is_deal} />
              </button>
              <button
                className={`row-save-action${saved?.is_alert ? " is-saved" : ""}`}
                type="button"
                title={saved?.is_alert ? "Убрать из оповещений" : "Добавить в оповещения"}
                aria-label={saved?.is_alert ? "Убрать из оповещений" : "Добавить в оповещения"}
                onClick={(event) => {
                  event.stopPropagation();
                  onSaveOpportunity(row.original, "alert");
                }}
              >
                <HeartIcon isSaved={saved?.is_alert} />
              </button>
            </div>
          );
        },
      }),
    ],
    [copyItemName, formatPrice, now, onSaveOpportunity, priceOverrides, savedByKey],
  );
  // TanStack Table intentionally exposes mutable callbacks; React Compiler skips this hook.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: getOpportunityKey,
  });
  const visibleColumns = table.getVisibleLeafColumns().length;

  return (
    <section className="comparison-panel" aria-label="Таблица сравнения">
      <div className="panel-toolbar">
        <ActiveFilterChips
          filters={filters}
          markets={markets}
          searchQuery={searchQuery}
          onFiltersChange={onFiltersChange}
          onSearchQueryChange={onSearchQueryChange}
        />
        <div className="table-result-state">
          {isRefreshing && (
            <RefreshCw className="is-spinning" aria-label="Обновление" />
          )}
          <span>
            Найдено:{" "}
            {new Intl.NumberFormat("ru-RU").format(totalMatches || rows.length)}
          </span>
        </div>
      </div>

      {errorMessage && (
        <div className="table-error" role="alert">
          {errorMessage}
        </div>
      )}

      <div className="comparison-table-scroll" onScroll={handleScroll}>
        <table className="comparison-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {isInitialLoading && (
            <TableSkeletonRows
              count={loadingRowCount}
              visibleColumns={visibleColumns}
            />
          )}

          {!isInitialLoading &&
            table.getRowModel().rows.map((row) => (
              <DealTableRow
                key={row.id}
                row={row}
                currencyCode={currency.code}
                isExpanded={expandedId === row.id}
                visibleColumns={visibleColumns}
                profitableSpreadPct={profitableSpreadPct}
                onToggle={toggleRow}
                priceOverride={priceOverrides[row.id]}
                onPriceSelect={selectLadderPrice}
              />
            ))}
          {isLoadingMore && (
            <TableSkeletonRows
              count={loadingRowCount}
              visibleColumns={visibleColumns}
            />
          )}
        </table>
      </div>

      {!isInitialLoading && rows.length === 0 && !errorMessage && (
        <div className="table-empty">
          <GitCompareArrows aria-hidden="true" />
          <strong>Подходящих сделок пока нет</strong>
          <span>Измените фильтры или обновите данные маркетов</span>
        </div>
      )}

      {copyNoticeId !== null && (
        <div className="copy-toast" key={copyNoticeId} role="status">
          <Check aria-hidden="true" />
          <span>Название предмета скопировано</span>
        </div>
      )}
    </section>
  );
}
